import React, { useRef, useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ExperienceCard from "./ExperienceCard";
import SkeletonCard from "../common/SkeletonCard";
import FarmCardStyle from "../../assets/stylesheets/FarmCard.module.css";

export default function ExperienceList({ 
  title, 
  subtitle, 
  experiences = [],
  onExperienceClick,
  loading = false
}) {
  // Ref to access Swiper instance and navigation elements
  const swiperRef = useRef(null);
  // Track if we're at the beginning/end of the carousel to disable nav buttons
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  // Helper to update nav button states based on current swiper position
  const updateNavState = (swiper) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  // Listen for slide changes to update nav button disabled states
  useEffect(() => {
    const swiper = swiperRef.current?.swiper;
    if (!swiper) return;
    
    const onChange = () => {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    };
    
    swiper.on("slideChange", onChange);
    onChange(); // Set initial state
    
    return () => swiper.off("slideChange", onChange);
  }, [experiences]);

  // Early exit: don't render section if no data and not loading
  if (!loading && (!experiences || experiences.length === 0)) {
    return null;
  }

  // Render either skeleton loaders or actual experience cards
  const renderContent = () => {
    if (loading) {
      // Show 4 placeholder cards while fetching data
      return Array.from({ length: 4 }).map((_, index) => (
        <SwiperSlide key={`skeleton-${index}`} style={{ width: "auto" }}>
          <SkeletonCard />
        </SwiperSlide>
      ));
    }

    // Map experiences to clickable cards
    return experiences.map((experience, index) => (
      <SwiperSlide key={experience.id} style={{ width: "auto" }}>
        <ExperienceCard
          experience={experience}
          index={index}
          onClick={() => onExperienceClick?.(experience)}
        />
      </SwiperSlide>
    ));
  };

  return (
    <section className={FarmCardStyle["events-section"]}>
      <div className={FarmCardStyle["events-container"]}>
        {/* Header with title and carousel navigation buttons */}
        <div className={FarmCardStyle["events-header"]}>
          <div>
            <h2 className={FarmCardStyle["events-title"]}>{title}</h2>
            {subtitle && (
              <p className={FarmCardStyle["events-subtitle"]}>{subtitle}</p>
            )}
          </div>
          
          {/* Navigation buttons - disabled at carousel boundaries */}
          <div className={FarmCardStyle["event-card-arrow"]}>
            <button
              ref={(node) => (swiperRef.current = { ...swiperRef.current, prevEl: node })}
              className={`${FarmCardStyle["swiper-prev"]} ${
                isBeginning ? FarmCardStyle["swiper-disabled"] : FarmCardStyle["swiper-active"]
              }`}
              aria-label="Previous slide"
            >
              <ChevronLeft className={FarmCardStyle["custom-arrow-icon"]} />
            </button>
            <button
              ref={(node) => (swiperRef.current = { ...swiperRef.current, nextEl: node })}
              className={`${FarmCardStyle["swiper-next"]} ${
                isEnd ? FarmCardStyle["swiper-disabled"] : FarmCardStyle["swiper-active"]
              }`}
              aria-label="Next slide"
            >
              <ChevronRight className={FarmCardStyle["custom-arrow-icon"]} />
            </button>
          </div>
        </div>

        {/* Responsive carousel with custom navigation and free-scroll mode */}
        <Swiper
          ref={swiperRef}
          modules={[FreeMode, Navigation]}
          spaceBetween={15}
          slidesPerView="auto"
          freeMode={true}
          navigation={{
            prevEl: swiperRef.current?.prevEl,
            nextEl: swiperRef.current?.nextEl,
          }}
          onSlideChange={updateNavState}
          onSwiper={updateNavState}
          breakpoints={{
            320: { slidesPerView: 1.2, spaceBetween: 12 },
            480: { slidesPerView: 1.5, spaceBetween: 12 },
            640: { slidesPerView: 2, spaceBetween: 15 },
            768: { slidesPerView: 2.5, spaceBetween: 15 },
            1024: { slidesPerView: 3, spaceBetween: 15 },
            1280: { slidesPerView: 4, spaceBetween: 15 },
          }}
        >
          {renderContent()}
        </Swiper>
      </div>
    </section>
  );
}
